/**
 * Period Selector Bar
 *
 * Compact period display that sits below the navigation bar
 * Both current and comparison periods are clickable dropdowns
 */

import { ChevronDown } from "lucide-react";
import { DateTime } from "luxon";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSalonStore } from "@/lib/store/salon";
import { formatLocalizedDate } from "../lib/format-localized-date";
import { useReportsV2Store } from "../store/use-reports-v2-store";
import { ComparisonStrategy, TimeRangePreset } from "../types";
import { SpecificMonthPicker } from "./specific-month-picker";

interface PeriodInfo {
  primary: string;
  secondary?: string;
}

export function PeriodSelectorBar() {
  const { t, i18n } = useTranslation("reports");
  const { currentSalon } = useSalonStore();
  const {
    currentPeriod,
    comparisonPeriod,
    timeRange,
    setTimeRangePreset,
    setSpecificMonth,
    setComparisonStrategy,
  } = useReportsV2Store();
  const [open, setOpen] = useState(false);

  if (!currentPeriod || !comparisonPeriod || !currentSalon) {
    return null;
  }

  const handlePresetSelect = (preset: TimeRangePreset) => {
    setTimeRangePreset(preset);
  };

  const handleComparisonStrategySelect = (strategy: ComparisonStrategy) => {
    setComparisonStrategy(strategy);
  };

  const formatDate = (date: Date, includeYear = false) => {
    const dt = DateTime.fromJSDate(date, { zone: currentSalon.timezone });
    return includeYear ? dt.toFormat("dd/MM/yyyy") : dt.toFormat("dd/MM");
  };

  const formatDateRange = (start: Date, end: Date, includeYear = false) =>
    `${formatDate(start)} - ${formatDate(end, includeYear)}`;

  const getCurrentPeriodInfo = (): PeriodInfo => {
    if (timeRange.preset === "CUSTOM" && timeRange.customRange) {
      return { primary: timeRange.customRange.label };
    }

    const startDate = currentPeriod.startDate;
    const endDate = currentPeriod.endDate;

    switch (timeRange.preset) {
      case TimeRangePreset.LAST_7_DAYS:
        return {
          primary: t("periodSelector.presets.last7Days"),
          secondary: formatDateRange(startDate, endDate),
        };
      case TimeRangePreset.LAST_30_DAYS:
        return {
          primary: t("periodSelector.presets.last30Days"),
          secondary: formatDateRange(startDate, endDate),
        };
      case TimeRangePreset.LAST_90_DAYS:
        return {
          primary: t("periodSelector.presets.last90Days"),
          secondary: formatDateRange(startDate, endDate),
        };
      case TimeRangePreset.SPECIFIC_MONTH: {
        if (!timeRange.specificMonth) return { primary: currentPeriod.label };
        const dt = DateTime.fromObject({
          year: timeRange.specificMonth.year,
          month: timeRange.specificMonth.month,
        });
        return { primary: formatLocalizedDate(dt, "MMMM yyyy", i18n.language) };
      }
      case TimeRangePreset.LAST_WEEK:
        return {
          primary: t("periodSelector.presets.lastWeek"),
          secondary: formatDateRange(startDate, endDate),
        };
      case TimeRangePreset.LAST_MONTH: {
        const dt = DateTime.fromJSDate(startDate, { zone: currentSalon.timezone });
        return {
          primary: t("periodSelector.presets.lastMonth"),
          secondary: dt.toFormat("MM/yyyy"),
        };
      }
      case TimeRangePreset.LAST_QUARTER: {
        const dt = DateTime.fromJSDate(startDate, { zone: currentSalon.timezone });
        return {
          primary: t("periodSelector.presets.lastQuarter"),
          secondary: `Q${dt.quarter} ${dt.year}`,
        };
      }
      case TimeRangePreset.LAST_YEAR: {
        const dt = DateTime.fromJSDate(startDate, { zone: currentSalon.timezone });
        return {
          primary: t("periodSelector.presets.lastYear"),
          secondary: `${dt.year}`,
        };
      }
      default:
        return { primary: currentPeriod.label };
    }
  };

  const getComparisonInfo = (): PeriodInfo => {
    const strategyKey =
      timeRange.comparisonStrategy === ComparisonStrategy.SAME_PERIOD_LAST_YEAR
        ? "samePeriodLastYear"
        : "previousPeriod";
    return {
      primary: t(`periodSelector.comparisonStrategies.${strategyKey}`),
      secondary: formatDateRange(comparisonPeriod.startDate, comparisonPeriod.endDate, true),
    };
  };

  const currentInfo = getCurrentPeriodInfo();
  const comparisonInfo = getComparisonInfo();

  return (
    <div className="flex items-stretch justify-center mx-auto max-w-2xl">
      {/* Current Period Selector Dropdown */}
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="!h-auto flex-1 min-w-0 flex flex-col items-center justify-center gap-1 font-normal py-2 px-3 sm:py-2.5 sm:px-4 text-center rounded-r-none border-r-0 active:bg-accent/70"
          >
            <span className="text-xs text-muted-foreground leading-none">
              {t("periodSelector.current")}
            </span>
            <div className="flex items-center justify-center gap-1.5 min-w-0">
              <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
              <span className="font-semibold text-sm whitespace-nowrap">{currentInfo.primary}</span>
              <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            </div>
            {currentInfo.secondary && (
              <span className="text-[11px] text-muted-foreground whitespace-nowrap leading-none">
                {currentInfo.secondary}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-64">
          <DropdownMenuLabel>{t("periodSelector.quickRanges")}</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => handlePresetSelect(TimeRangePreset.LAST_7_DAYS)}>
            {t("periodSelector.presets.last7Days")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handlePresetSelect(TimeRangePreset.LAST_30_DAYS)}>
            {t("periodSelector.presets.last30Days")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handlePresetSelect(TimeRangePreset.LAST_90_DAYS)}>
            {t("periodSelector.presets.last90Days")}
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuLabel>{t("periodSelector.lastPeriod")}</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => handlePresetSelect(TimeRangePreset.LAST_WEEK)}>
            {t("periodSelector.presets.lastWeek")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handlePresetSelect(TimeRangePreset.LAST_MONTH)}>
            {t("periodSelector.presets.lastMonth")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handlePresetSelect(TimeRangePreset.LAST_QUARTER)}>
            {t("periodSelector.presets.lastQuarter")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handlePresetSelect(TimeRangePreset.LAST_YEAR)}>
            {t("periodSelector.presets.lastYear")}
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuLabel>{t("periodSelector.specificMonth")}</DropdownMenuLabel>
          <SpecificMonthPicker
            locale={i18n.language}
            selected={
              timeRange.preset === TimeRangePreset.SPECIFIC_MONTH
                ? timeRange.specificMonth
                : undefined
            }
            onSelect={(year, month) => {
              setSpecificMonth(year, month);
              setOpen(false);
            }}
          />
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Comparison Period Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="!h-auto flex-1 min-w-0 flex flex-col items-center justify-center gap-1 font-normal py-2 px-3 sm:py-2.5 sm:px-4 text-center rounded-l-none active:bg-accent/70"
          >
            <span className="text-xs text-muted-foreground leading-none">
              {t("periodSelector.compare")}
            </span>
            <div className="flex items-center justify-center gap-1.5 min-w-0">
              <div className="w-2 h-2 bg-gray-400 rounded-full flex-shrink-0" />
              <span className="font-medium text-sm whitespace-nowrap">
                {comparisonInfo.primary}
              </span>
              <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            </div>
            {comparisonInfo.secondary && (
              <span className="text-[11px] text-muted-foreground whitespace-nowrap leading-none">
                {comparisonInfo.secondary}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-56">
          <DropdownMenuLabel>{t("periodSelector.comparisonStrategy")}</DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => handleComparisonStrategySelect(ComparisonStrategy.PREVIOUS_PERIOD)}
          >
            {t("periodSelector.comparisonStrategies.previousPeriod")}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => handleComparisonStrategySelect(ComparisonStrategy.SAME_PERIOD_LAST_YEAR)}
          >
            {t("periodSelector.comparisonStrategies.samePeriodLastYear")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
